import {
  UseFormGetValues,
  UseFormSetValue,
  UseFormStateReturn,
} from 'react-hook-form';
import { formatCurrency } from 'src/utils/numberFormatter';
import { posChangedField, posField } from 'src/utils/posFieldUtils';
import { PurchaseOrderDetailsInput } from 'src/utils/purchaseUtils';
import { getCosts } from 'src/utils/ticketUtils';
import {
  PosFieldOfNullableDecimal,
  PurchasePayment,
  PurchasePaymentStatus,
  TicketGroupInput,
  UiCurrency,
  UiMoney,
} from 'src/WebApiController';

import { PaymentDetailsTotals } from './PaymentDetailsTotals/PaymentDetailsTotals';

/**
 * PosField<decimal?> money fields on TicketGroupInput that should be
 * converted when toggling between currencies.
 */
const TICKET_GROUP_MONEY_POS_FIELDS: (keyof TicketGroupInput)[] = [
  'unitCost',
  'expectedValue',
  'faceValueCost',
  'totalCost',
  'taxPaid',
  'deliveryCost',
];

/**
 * Convert all money fields from the target (native) currency to the
 * original currency by dividing each value by the conversion rate.
 *
 * Call this when the user toggles FROM target TO original currency.
 */
export function convertToOriginalCurrency(
  conversionRate: number,
  originalCurrencyCode: string,
  getUiCurrency: (currencyCode: string | null | undefined) => UiCurrency,
  setValue: UseFormSetValue<PurchaseOrderDetailsInput>,
  getValues: UseFormGetValues<PurchaseOrderDetailsInput>
) {
  if (conversionRate <= 0) return;

  const uiCurrency = getUiCurrency(originalCurrencyCode);

  const ticketGroups = getValues('ticketGroups') ?? [];

  const convertedTicketGroups = ticketGroups.map((tg) => {
    const updated = { ...tg };

    // Convert PosField money fields
    for (const field of TICKET_GROUP_MONEY_POS_FIELDS) {
      const pf = tg[field] as PosFieldOfNullableDecimal | null;
      if (pf?.value != null) {
        (updated as Record<string, unknown>)[field] = posField(
          pf.value / conversionRate,
          pf.hasChanged
        );
      }
    }

    // Convert unitFullyLoadedCost (plain number, read-only)
    if (tg.unitFullyLoadedCost != null) {
      updated.unitFullyLoadedCost = tg.unitFullyLoadedCost / conversionRate;
    }

    // Swap ticket group currency code
    updated.currencyCode = posField(originalCurrencyCode, false);

    return updated;
  });

  setValue('ticketGroups', convertedTicketGroups);

  // Convert additional costs (preserve original hasChanged state)
  const additionalCosts = getValues('additionalCosts');
  if (additionalCosts?.value?.length) {
    const wasAlreadyChanged = !!additionalCosts.hasChanged;
    const convertedCosts = additionalCosts.value.map((c) => ({
      ...c,
      cost: c.cost
        ? {
            dec: uiCurrency.dec,
            amt: c.cost.amt / conversionRate,
            currency: originalCurrencyCode,
            disp: formatCurrency(
              Number(c.cost.amt / conversionRate),
              uiCurrency.code,
              uiCurrency.dec
            ),
          }
        : c.cost,
    }));
    setValue(
      'additionalCosts',
      wasAlreadyChanged
        ? posChangedField(convertedCosts)
        : posField(convertedCosts, false)
    );
  }
}

/**
 * Convert all money fields from the original currency back to the
 * target (native) currency.
 *
 * - Fields where `hasChanged === false` are restored to their exact
 *   value from `formState.defaultValues` (no rounding drift).
 * - Fields where `hasChanged === true` (user edited in original
 *   currency) are reverse-converted by multiplying by the rate.
 *
 * Call this when the user toggles FROM original BACK TO target currency.
 */
export function convertToTargetCurrency(
  conversionRate: number,
  targetCurrencyCode: string,
  getUiCurrency: (currencyCode: string | null | undefined) => UiCurrency,
  defaultValues: UseFormStateReturn<PurchaseOrderDetailsInput>['defaultValues'],
  setValue: UseFormSetValue<PurchaseOrderDetailsInput>,
  getValues: UseFormGetValues<PurchaseOrderDetailsInput>
) {
  if (conversionRate <= 0) return;

  const ticketGroups = getValues('ticketGroups') ?? [];
  const defaultTicketGroups = defaultValues?.ticketGroups ?? [];

  const restoredTicketGroups = ticketGroups.map((tg, tgIndex) => {
    const defaultTg = defaultTicketGroups[tgIndex];
    const updated = { ...tg };

    // Restore PosField money fields
    for (const field of TICKET_GROUP_MONEY_POS_FIELDS) {
      const pf = tg[field] as PosFieldOfNullableDecimal | null;
      if (pf == null) continue;

      if (!pf.hasChanged && defaultTg) {
        // Not edited — restore exact default value
        const defaultPf = defaultTg[
          field as keyof typeof defaultTg
        ] as PosFieldOfNullableDecimal | null;
        (updated as Record<string, unknown>)[field] = posField(
          defaultPf?.value,
          false
        );
      } else if (pf.value != null) {
        // User edited — reverse-convert
        (updated as Record<string, unknown>)[field] = posChangedField(
          pf.value * conversionRate
        );
      }
    }

    // Restore unitFullyLoadedCost from defaults (read-only, always restore)
    if (defaultTg) {
      updated.unitFullyLoadedCost =
        (defaultTg as TicketGroupInput).unitFullyLoadedCost ?? null;
    } else if (tg.unitFullyLoadedCost != null) {
      updated.unitFullyLoadedCost = tg.unitFullyLoadedCost * conversionRate;
    }

    // Restore ticket group currency code
    updated.currencyCode = posField(targetCurrencyCode, false);

    return updated;
  });

  setValue('ticketGroups', restoredTicketGroups);

  // Restore additional costs
  const additionalCosts = getValues('additionalCosts');
  if (additionalCosts?.value?.length) {
    const uiCurrency = getUiCurrency(targetCurrencyCode);

    if (additionalCosts.hasChanged) {
      // User modified costs — reverse-convert all amounts
      const restoredCosts = additionalCosts.value.map((c) => ({
        ...c,
        cost: c.cost
          ? {
              dec: uiCurrency.dec,
              amt: c.cost.amt * conversionRate,
              currency: targetCurrencyCode,
              disp: formatCurrency(
                Number(c.cost.amt * conversionRate),
                uiCurrency.code,
                uiCurrency.dec
              ),
            }
          : c.cost,
      }));
      setValue('additionalCosts', posChangedField(restoredCosts));
    } else {
      // No edits — restore exact defaults
      const defaultCosts =
        defaultValues?.additionalCosts as PurchaseOrderDetailsInput['additionalCosts'];
      if (defaultCosts) {
        setValue('additionalCosts', defaultCosts);
      }
    }
  }
}

/**
 * Recalculate `unitFullyLoadedCost` for each ticket group as a preview.
 *
 * This mirrors the backend logic in `TicketCostHelper`:
 *   1. weightedPOFee = poFee × tgUnitCost / poTotalTicketCost
 *   2. deliveryFeePerTicket = tgDeliveryCost / tgNumberOfTickets
 *   3. unitFullyLoadedCost = tgUnitCost + weightedPOFee + deliveryFeePerTicket
 *
 * Note: overpaid multiplier is NOT applied (matches backend behaviour
 * where `applyPurchasePaymentLogic = false` for unitFullyLoadedCost).
 */
export function recalculateUnitFullyLoadedCostPreview(
  setValue: UseFormSetValue<PurchaseOrderDetailsInput>,
  getValues: UseFormGetValues<PurchaseOrderDetailsInput>
) {
  const ticketGroups = getValues('ticketGroups') ?? [];
  const additionalCosts = getValues('additionalCosts');

  const { totalTicketCost, totalShipping, totalCost } = getCosts(
    ticketGroups,
    additionalCosts?.value
  );

  // PO fee = additional costs only (exclude ticket cost and shipping)
  const poFee = totalCost - totalTicketCost - totalShipping;

  const updatedTicketGroups = ticketGroups.map((tg) => {
    const unitCost = tg.unitCost?.value ?? 0;
    const deliveryCost = tg.deliveryCost?.value ?? 0;
    const numberOfTickets = tg.numberOfTickets?.value ?? 0;

    let unitFullyLoadedCost = unitCost;

    // Weighted PO fee allocation
    if (totalTicketCost > 0) {
      unitFullyLoadedCost += (poFee * unitCost) / totalTicketCost;
    }

    // Delivery cost per ticket
    if (numberOfTickets > 0) {
      unitFullyLoadedCost += deliveryCost / numberOfTickets;
    }

    return { ...tg, unitFullyLoadedCost };
  });

  setValue('ticketGroups', updatedTicketGroups);
}

/**
 * POS-8906: resolves the payments totals to display below the payments table.
 *
 * Total Paid is read from the stored purchase order value (which handles
 * currency conversion). Outstanding Balance is derived from the current total
 * purchase cost so it reacts to line-item edits before the purchase order is
 * saved. The totals are only returned while they are still in sync with the
 * displayed payments (feature enabled, not editing and with no unsaved payment
 * changes) and when there is at least one paid payment.
 */
export const getPaymentDetailsTotals = ({
  featureEnabled,
  isEditing,
  paymentsChanged,
  payments,
  totalPmt,
  totalCost,
}: {
  featureEnabled: boolean;
  isEditing: boolean;
  paymentsChanged: boolean;
  payments?: PurchasePayment[] | null;
  totalPmt?: UiMoney | null;
  totalCost: number;
}): PaymentDetailsTotals | null => {
  if (!featureEnabled || isEditing || paymentsChanged) {
    return null;
  }

  if (totalPmt == null) {
    return null;
  }

  const hasPaidPayment = (payments ?? []).some(
    (payment) => payment.paymentStatus === PurchasePaymentStatus.Paid
  );

  if (!hasPaidPayment) {
    return null;
  }

  return {
    totalPaid: totalPmt,
    outstandingBalance: totalCost - totalPmt.amt,
  };
};
